// Telegram Mini App integration. All of this is inert outside Telegram: the
// SDK (loaded in index.html) sets window.Telegram.WebApp with an EMPTY initData
// in a normal browser, so `isTelegram()` is false and the regular web app runs
// completely unchanged.

interface TgWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: { id: number; first_name?: string; last_name?: string; username?: string };
  };
  colorScheme?: 'light' | 'dark';
  ready(): void;
  expand(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
}

function webApp(): TgWebApp | null {
  const tg = (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp;
  // A real launch carries a signed, non-empty initData string. Outside Telegram
  // the object exists but initData is '' — treat that as "not Telegram".
  return tg && typeof tg.initData === 'string' && tg.initData.length > 0 ? tg : null;
}

/** True only inside a genuine Telegram Mini App launch. */
export function isTelegram(): boolean {
  return webApp() !== null;
}

/** Take over the viewport and paint the chrome in the neon-noir background. */
export function initTelegram(): void {
  const tg = webApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    tg.disableVerticalSwipes?.();
    tg.setHeaderColor?.('#070A12');
    tg.setBackgroundColor?.('#070A12');
  } catch {
    /* older Telegram clients miss some methods — non-fatal */
  }
  document.documentElement.classList.add('tg');
}

/** The signed launch string to hand to the telegramAuth callable. */
export function tgInitData(): string | null {
  return webApp()?.initData ?? null;
}

/** The launching user's display name, for the default handle. */
export function tgUserName(): string | null {
  const u = webApp()?.initDataUnsafe?.user;
  if (!u) return null;
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return (full || u.username || '').slice(0, 24) || null;
}
